import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Users,
  Presentation,
  Heart,
  Calendar,
  MapPin,
  Wifi,
  Camera,
  UtensilsCrossed,
  Music,
  Sparkles,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getLocalizedUrl } from "@/utils/languageUtils";
import EventContactModal from "@/components/EventContactModal";

const EventsSection = () => {
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const [isEventContactModalOpen, setIsEventContactModalOpen] = useState(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const timers = useRef<Map<number, NodeJS.Timeout>>(new Map());

  // Get language and translation function
  const { t, language } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const cardIndex = parseInt(
            entry.target.getAttribute("data-card-index") || "0",
          );

          if (entry.isIntersecting) {
            // Card has entered the screen, start timer
            const timer = setTimeout(() => {
              setVisibleCards((prev) => {
                const newSet = new Set(prev);
                newSet.add(cardIndex);
                return newSet;
              });
            }, 2000); // 2 seconds

            // Store timer reference
            timers.current.set(cardIndex, timer);
          } else {
            // Card has left the screen, clear timer and hide content
            const timer = timers.current.get(cardIndex);
            if (timer) {
              clearTimeout(timer);
              timers.current.delete(cardIndex);
            }

            setVisibleCards((prev) => {
              const newSet = new Set(prev);
              newSet.delete(cardIndex);
              return newSet;
            });
          }
        });
      },
      {
        threshold: 0,
        rootMargin: "0px 0px 0px 0px",
      },
    );

    cardRefs.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => {
      observer.disconnect();
      // Clear all timers on cleanup
      timers.current.forEach((timer) => clearTimeout(timer));
      timers.current.clear();
    };
  }, []);
  const eventTypes = [
    {
      icon: Presentation,
      title: t("events.seminarRooms"),
      description: t("events.seminarRoomsDesc"),
      features: [
        t("events.hdProjectors"),
        t("events.videoConferencing"),
        t("events.wirelessPresentation"),
        t("events.breakoutSpaces"),
      ],

      image:
        "/images/Homepage%20Shortlisted%20media/seminar%20room%202.webp",
    },
    {
      icon: UtensilsCrossed,
      title: t("events.banquetHalls"),
      description: t("events.banquetHallsDesc"),
      features: [
        t("events.fineDiningSetup"),
        t("events.professionalCatering"),
        t("events.flexibleSeating"),
        t("events.seating200"),
      ],

      image:
        "/images/Homepage%20Shortlisted%20media/banquet%20hall%202.webp",
    },
    {
      icon: Heart,
      title: t("events.weddingVenues"),
      description: t("events.weddingVenuesDesc"),
      features: [
        t("events.alpineViews"),
        t("events.weddingPlanning"),
        t("events.photographySpots"),
        t("events.receptionAreas"),
      ],

      image: "/images/Homepage%20Shortlisted%20media/wedding_venue.webp",
    },
  ];

  const additionalFeatures = [
    {
      icon: Camera,
      title: t("events.digitalLedWall"),
      description: t("events.perfectForOccasionDesc"),
    },
    {
      icon: Wifi,
      title: t("events.highSpeedInternet"),
      description: t("events.perfectForOccasionDesc"),
    },
    {
      icon: Music,
      title: t("events.audioSystems"),
      description: t("events.perfectForOccasionDesc"),
    },
    {
      icon: Sparkles,
      title: t("events.eventPlanning"),
      description: t("events.perfectForOccasionDesc"),
    },
  ];

  return (
    <section className="py-12 md:py-20 bg-white" data-oid="qlj23ft">
      <div
        className="max-w-7xl mx-auto px-4 md:px-8 relative"
        data-oid="w4g7wda"
      >
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
          data-oid="q69i9dw"
        >
          <h2
            className="text-4xl md:text-5xl lg:text-6xl text-gray-900 font-playfair mb-6 uppercase"
            data-oid="0ibqi4m"
          >
            {t("events.title")}
          </h2>
          <p
            className="text-xl text-gray-900 font-lato max-w-4xl mx-auto leading-relaxed"
            data-oid="gp.:060"
          >
            {t("events.description")}
          </p>
        </motion.div>

        {/* Main Event Types */}
        <div
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20 md:flex md:flex-wrap md:justify-center md:gap-8"
          data-oid="j67x7t:"
        >
          {eventTypes.map((event, index) => {
            const Icon = event.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group"
                ref={(el) => (cardRefs.current[index] = el)}
                data-card-index={index}
                data-oid="0upyk25"
              >
                <div
                  className="relative w-full max-w-sm mx-auto md:w-80 lg:max-w-none h-80 md:h-96 rounded-2xl overflow-hidden bg-cover bg-center transition-transform duration-300 group-hover:-translate-y-2 shadow-xl hover:shadow-2xl"
                  style={{ backgroundImage: `url(${event.image})` }}
                  data-oid="wz-xds7"
                >
                  {/* Top Gradient Overlay for Title Readability */}
                  <div
                    className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/60 to-transparent"
                    data-oid="uhhag45"
                  ></div>

                  {/* Title and Icon at Top */}
                  <div
                    className="absolute top-0 left-0 right-0 p-6"
                    data-oid=".u367c7"
                  >
                    <div
                      className="flex items-start justify-between"
                      data-oid="j.actu-"
                    >
                      <h3
                        className="font-playfair font-semibold text-elegant-white text-base sm:text-lg leading-tight flex-1 pr-4 drop-shadow-lg break-words hyphens-auto min-h-[2.5rem] flex items-center"
                        data-oid="hcou9tk"
                      >
                        {event.title}
                      </h3>
                      <div
                        className="w-12 h-12 bg-elegant-white/90 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0"
                        data-oid="dh4udem"
                      >
                        <Icon
                          className="text-gray-900"
                          size={24}
                          data-oid="lw-4a_o"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Content Overlay at Bottom - Hidden by default, shows on hover (desktop) or when card reaches 42px below viewport (mobile) */}
                  <div
                    className={`absolute bottom-0 left-0 right-0 bg-elegant-white/95 backdrop-blur-sm rounded-b-2xl p-6 transition-all duration-300 ${
                      visibleCards.has(index)
                        ? "translate-y-0 opacity-100 lg:translate-y-full lg:opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100"
                        : "translate-y-full opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100"
                    }`}
                    data-oid="h6a10x4"
                  >
                    <p
                      className="font-lato text-gray-900 text-sm leading-relaxed mb-4"
                      data-oid="gdt7lw7"
                    >
                      {event.description}
                    </p>

                    {/* Features */}
                    <div className="space-y-2 mb-4" data-oid="qvrx999">
                      {event.features.map((feature, featureIndex) => (
                        <div
                          key={featureIndex}
                          className="flex items-center gap-2"
                          data-oid="6pcn93h"
                        >
                          <div
                            className="w-1.5 h-1.5 bg-alpine-gold rounded-full flex-shrink-0"
                            data-oid="1l-3whp"
                          ></div>
                          <span
                            className="text-gray-900 font-lato text-xs"
                            data-oid="0honjix"
                          >
                            {feature}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <a
                      href={`${getLocalizedUrl("/events", language)}#consult`}
                      data-oid="nle1rbf"
                    >
                      <Button
                        variant="luxury"
                        size="sm"
                        className="w-full rounded-full py-2 text-sm font-semibold transition-all duration-300"
                        data-oid="-6_ctc1"
                      >
                        {t("events.learnMore")}
                      </Button>
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Additional Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          viewport={{ once: true }}
          className="mb-16"
          data-oid="gz.:k:i"
        >
          <h3
            className="text-3xl text-gray-900 font-playfair text-center mb-12 uppercase"
            data-oid="2tqjktj"
          >
            Premium Event Features
          </h3>

          {/* Mobile: Simple text badges with logos */}
          <div className="block md:hidden" data-oid="lsz75uu">
            <div
              className="flex flex-wrap justify-center gap-3"
              data-oid="xdvq4ak"
            >
              {additionalFeatures.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-all duration-300"
                    data-oid="km21qy7"
                  >
                    <div
                      className="w-6 h-6 bg-gradient-to-br from-alpine-gold to-yellow-600 rounded-full flex items-center justify-center flex-shrink-0"
                      data-oid="jgmxody"
                    >
                      <Icon className="w-3 h-3 text-white" data-oid="jx5rk7h" />
                    </div>
                    <span
                      className="text-sm font-semibold text-gray-900 font-playfair whitespace-nowrap"
                      data-oid="ckpxgny"
                    >
                      {feature.title}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Desktop: Full cards */}
          <div
            className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6"
            data-oid="ii.apon"
          >
            {additionalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 text-center group hover:-translate-y-1"
                  data-oid="7i_fzvy"
                >
                  <div
                    className="w-12 h-12 bg-gradient-to-br from-alpine-gold to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                    data-oid="19nv4_-"
                  >
                    <Icon className="w-6 h-6 text-white" data-oid="1t69i8t" />
                  </div>
                  <h4
                    className="text-lg font-semibold text-gray-900 font-playfair mb-2"
                    data-oid="3cn17bb"
                  >
                    {feature.title}
                  </h4>
                  <p
                    className="text-gray-900 font-lato text-sm"
                    data-oid="q0pi8y_"
                  >
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Target Audiences */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-navy to-slate-800 rounded-3xl p-8 md:p-12 text-white"
          data-oid="k0dhu02"
        >
          <div className="text-center mb-8" data-oid="b17.rys">
            <h3
              className="text-3xl font-playfair mb-4 text-gold"
              data-oid="_tq3yqj"
            >
              {t("events.perfectForOccasion")}
            </h3>
            <p
              className="text-white/90 font-lato text-lg max-w-3xl mx-auto"
              data-oid="v.be3im"
            >
              {t("events.perfectForOccasionDesc")}
            </p>
          </div>

          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-6 items-start"
            data-oid=":yx38wc"
          >
            {[
              {
                title: t("events.privateParties"),
                description: t("events.privatePartiesDesc"),
              },
              {
                title: t("events.groups"),
                description: t("events.groupsDesc"),
              },
              {
                title: t("events.wellnessRetreats"),
                description: t("events.wellnessRetreatsDesc"),
              },
              {
                title: t("events.corporateEvents"),
                description: t("events.corporateEventsDesc"),
              },
            ].map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="text-center p-4 bg-white/10 rounded-2xl backdrop-blur-sm border border-gold/20 h-full flex flex-col"
                data-oid="lyb:1zg"
              >
                <div className="flex-1" data-oid="ql4m_y0">
                  <h4
                    className="text-lg sm:text-xl font-semibold font-playfair mb-2 text-gold break-words hyphens-auto leading-tight"
                    data-oid="i03bbzj"
                  >
                    {audience.title}
                  </h4>
                  <p
                    className="text-white/80 font-lato text-sm leading-relaxed break-words hyphens-auto"
                    data-oid="vj0jmsq"
                  >
                    {audience.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Parking Information */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
          data-oid="ujo2wn3"
        >
          <div
            className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 max-w-2xl mx-auto"
            data-oid="obso748"
          >
            <div
              className="flex items-center justify-center gap-3 mb-4"
              data-oid="38l3yjz"
            >
              <MapPin className="w-8 h-8 text-alpine-gold" data-oid="54vah1y" />
              <h4
                className="text-2xl font-semibold text-gray-900 font-playfair"
                data-oid="yb7zigg"
              >
                {t("events.convenientParking")}
              </h4>
            </div>
            <p className="text-gray-900 font-lato text-lg" data-oid=":9kv3h5">
              {t("events.convenientParkingDesc")}
            </p>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-16"
          data-oid="1t.t0ha"
        >
          <div
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            data-oid="fx_til3"
          >
            <a href={getLocalizedUrl("/events", language)} data-oid="bars-a4">
              <Button
                variant="luxury"
                size="lg"
                className="text-lg px-8 py-4 font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                data-oid="0apc3yz"
              >
                {t("events.planYourEvent")}
              </Button>
            </a>

            <Button
              onClick={() => setIsEventContactModalOpen(true)}
              variant="ghost-light"
              size="lg"
              className="border-2 border-gold/50 text-gold hover:bg-gold/20 px-8 py-4 text-lg font-semibold rounded-full transition-all duration-300"
              trackingType="contact_modal"
              trackingLabel={t("events.requestQuote")}
              trackingLocation="homepage_events_section"
              trackingData={{ modalType: "event_planning" }}
              data-oid="i.uhsuw"
            >
              {t("events.requestQuote")}
            </Button>
          </div>

          <div
            className="flex items-center justify-center gap-2 mt-6 text-gold/80"
            data-oid="l8fsni:"
          >
            <Calendar className="w-5 h-5" data-oid="8vppiq4" />
            <span className="font-lato" data-oid="8dkla.o">
              {t("events.professionalPlanning")}
            </span>
          </div>
        </motion.div>
      </div>

      {/* Event Contact Modal */}
      <EventContactModal
        isOpen={isEventContactModalOpen}
        onClose={() => setIsEventContactModalOpen(false)}
        data-oid="events-section-modal"
      />
    </section>
  );
};

export default EventsSection;
